/**
 * Fetch media from brooklyn
 * @module services/media
 */
import { stringify as qsStringifyQuery } from 'qs'
import { Map, Set, fromJS } from 'immutable'
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import request from '../request'
import { createAuthorization } from '../auth'
import { getLocationPathname, getLocationQuery } from '../location'
import matchPath from '../../routes/match-path'

export const TYPE_PREVIEW = 'preview'
export const TYPE_FEATURE = 'feature'
export const MEDIA_STORE_KEY = 'media'
export const MEDIA_INFO_STORE_KEY = 'mediaInfo'
export const MEDIA_ID_REGEX = /^[0-9]+$/

/**
 * Given the location state get the media id
 * @param {Map} location The location state
 * @returns {String|undefined} The media id if found in the location
 */
export function getMediaIdFromLocation (location = Map()) {
  if (!Map.isMap(location)) {
    throw new Error('The location argument is required to be a Map')
  }
  const match = matchPath({
    pathname: getLocationPathname(location),
    path: '/media/:mediaId',
  })
  const mediaId = get(match, 'params.mediaId')
  return mediaId
}

/**
 * Return true when on a media detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is a media detail page
 */
export function isMediaDetailPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const id = getMediaIdFromLocation(location)
  return MEDIA_ID_REGEX.test(id)
}

/**
 * Return true when on a media detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is a media detail page
 */
export function isMediaCreatePage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const id = getMediaIdFromLocation(location)
  return id === 'create'
}

/**
 * Return true when on a media list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is a media detail page
 */
export function isMedialListPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/media',
  })
}

/**
 * Given state create the media list options
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 */
export function createMediaListViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    offset: query.get('offset') || 0,
    limit: query.get('limit') || 50,
    contentId: query.get('contentId'),
    mediaId: query.get('mediaId'),
    mediaMaestroTitleCode: query.get('mediaMaestroTitleCode'),
    use: query.get('use'),
    include: Set(['content_policy']),
  })
}

/**
 * Given state create the media detail options
 * @param {Object} state The parameters
 * @param {Object} state.location The location from state
 */
export function createMediaDetailViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return Map({
    mediaId: getMediaIdFromLocation(location),
    include: Set(['media_info', 'media_assets']),
  })
}

/**
 * Given state create the media create options
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 */
export function createMediaCreateViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return Map({ create: true })
}

/**
 * Given state create the media detail media info view options
 * @param {Object} state The parameters
 * @param {Map} state.media The media state
 */
export function createMediaDetailMediaInfoViewOptions (state = {}) {
  const { media } = state
  if (!Map.isMap(media)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return Map({
    mediaId: media.get('mediaId'),
  })
}

/**
 * Given state create the media detail media assets view options
 * @param {Object} options The parameters
 * @param {String|Set} options.mediaId The media options
 */
export function createMediaMediaAssetsViewOptions (options = {}) {
  const { mediaId } = options
  if (!Set.isSet(mediaId) && !mediaId) {
    throw new Error('The mediaId property is required options to be a Set or defined')
  }
  return Map({
    mediaId: Set.isSet(mediaId) ? mediaId : Set([mediaId]),
    include: Set(['media_assets_ingest_jobs', 'media_assets_ingest_urls']),
  })
}

/**
 * Get a single piece of media by contentId or mediaId
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String} options.contentId The contentId of the media
 * @param {Number} options.mediaId The mediaId of the media
 */
export async function getMedia (options = {}) {
  const { auth, server, contentId, mediaId } = options
  const params = omitBy({ contentId }, isUndefined)
  const res = await request
    .get(`${server}/v1/media${mediaId ? `/${mediaId}` : ''}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(qsStringifyQuery(params, { arrayFormat: 'indices', encodeValuesOnly: true }))
  return fromJS(get(res, 'body', {}))
}

/**
 * Create one media item
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Map} options.data The media data to create
 */
export async function upsertMedia (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  const res = await request
    .post(`${server}/v1/media`)
    .send(data.toJS())
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(get(res, 'body', {}))
}

/**
 * Get the media info for a single media item for video playback
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Number} options.mediaId The mediaId of the media
 */
export async function getMediaInfo (options = {}) {
  const { auth, server, mediaId } = options
  const res = await request
    .get(`${server}/media/${mediaId}`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a list of media from brooklyn
 */
export async function getMediaList (options = {}) {
  const { auth, server, offset, limit, contentId, mediaId, mediaMaestroTitleCode, use } = options
  const params = omitBy(
    {
      offset,
      limit,
      contentId,
      mediaId,
      mediaMaestroTitleCode,
      use,
    },
    isUndefined,
  )
  const res = await request
    .get(`${server}/v1/media/list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(qsStringifyQuery(params, { arrayFormat: 'indices', encodeValuesOnly: true }))
  return fromJS(get(res, 'body', {}))
}

/**
 * Delete a media item
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String|Number} options.mediaId The mediaId of the media to delete
 */
export async function deleteMedia (options = {}) {
  const { auth, server, mediaId } = options
  if (!mediaId) {
    throw new Error('The mediaId option is required')
  }
  const res = await request
    .delete(`${server}/v1/media/${mediaId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}
